import React, { Fragment } from 'react'
import _isArray from 'lodash/isArray'
import _isBoolean from 'lodash/isBoolean'
import _isNull from 'lodash/isNull'
import _isString from 'lodash/isString'
import _isUndefined from 'lodash/isUndefined'
import isValidEmail from '@purecarslabs/signal-utils/isValidEmail'
import {
  isUSPostalCode,
  isCanadaPostalCode
} from '@purecarslabs/signal-utils/postalCode'
import {
  CONTACT_METHOD_EMAIL,
  CONTACT_METHOD_TEXT
} from '@app-src/data/leads/constants'
import { LeadContactType } from '@app-src/data/leads/types'
import {
  LeadFormSpecFieldType,
  LeadFormSpecResponseType
} from '@app-src/data/lead-form-spec/types'
import { debugLogForms } from '@app-src/debug'
import {
  INPUT_NODE_SELECT,
  INPUT_NODE_TEXTAREA,
  INPUT_TYPE_EMAIL,
  INPUT_TYPE_PHONE,
  INPUT_TYPE_TEXT,
  CONTACT,
  EMAIL_OR_PHONE,
  EXTENSION_CATEGORY,
  FIELD_NAME,
  FIELDS,
  FORM_FIELD_ACCEPT_TERMS,
  FORM_FIELD_CONTACT_METHOD,
  FORM_FIELD_EMAIL,
  FORM_FIELD_FIRST_NAME,
  FORM_FIELD_LAST_NAME,
  FORM_FIELD_MESSAGE,
  FORM_FIELD_PHONE,
  FORM_FIELD_TERMS_CONDITIONS,
  FORM_FIELD_ZIP_CODE,
  IF_SPECIFIED,
  REQUIRED,
  TEXT_ME
} from './constants'

// eslint-disable-next-line max-len, no-useless-escape
const REGEX_VALIDATE_PHONE = /^\+?(1-)?\(?\d{3}\)?\s?-?\d{3}-?\d{4}$/

export const MODAL_LEAD_FORM_FIELDS_STANDARD = [
  FORM_FIELD_FIRST_NAME,
  FORM_FIELD_LAST_NAME,
  FORM_FIELD_EMAIL,
  FORM_FIELD_PHONE,
  FORM_FIELD_ZIP_CODE,
  FORM_FIELD_CONTACT_METHOD,
  FORM_FIELD_MESSAGE
]

export const MODAL_LEAD_FORM_FIELDS_TEXT_ME_THIS_INFO = [
  FORM_FIELD_FIRST_NAME,
  FORM_FIELD_LAST_NAME,
  FORM_FIELD_EMAIL,
  FORM_FIELD_PHONE,
  FORM_FIELD_MESSAGE
]

export const DEFAULT_FORM_SPEC = {
  [FIELDS]: [
    {
      [FIELD_NAME]: FORM_FIELD_FIRST_NAME
    },
    {
      [FIELD_NAME]: FORM_FIELD_LAST_NAME
    },
    {
      [FIELD_NAME]: FORM_FIELD_EMAIL
    },
    {
      [FIELD_NAME]: FORM_FIELD_PHONE,
      [REQUIRED]: IF_SPECIFIED
    },
    {
      [FIELD_NAME]: FORM_FIELD_ZIP_CODE
    },
    {
      [FIELD_NAME]: FORM_FIELD_CONTACT_METHOD,
      [EXTENSION_CATEGORY]: CONTACT
    }
  ]
} as LeadFormSpecResponseType

export const DEFAULT_FORM_SPEC_TEXT_ME_THIS_INFO = {
  [FIELDS]: [
    {
      [FIELD_NAME]: FORM_FIELD_FIRST_NAME
    },
    {
      [FIELD_NAME]: FORM_FIELD_LAST_NAME
    },
    {
      [FIELD_NAME]: FORM_FIELD_EMAIL
    },
    {
      [FIELD_NAME]: FORM_FIELD_PHONE
    }
  ]
} as LeadFormSpecResponseType

type ModalLeadFormFieldDefinitionType = {
  initialValue?: string | boolean
  id?: string
  name: string
  type: string
  description: string
  placeholder: string
  validateFunc?: (
    val: string | boolean,
    values: LeadContactType
  ) => string | null
}

/**
 * MODAL_LEAD_FORM_FIELD_DEFINITIONS
 * @description definitions for the all the modal form fields.  Each object in the array has the following members:
 * @key {String} name - the name of the field (and key in the resulting values object)
 * @key {String} type - the type of the field
 * @key {String} description - the text description of the field
 * @key {String} placeholder - the placeholder text to use for the field
 * @key {Function} validateFunc - if defined, a special function that will receive a value and return a boolean if it
 *   validates successfully
 * @key {JSX} children - children of the input, if needed, for instance to provide select options
 */
export const MODAL_LEAD_FORM_FIELD_DEFINITIONS = [
  {
    name: FORM_FIELD_FIRST_NAME,
    type: INPUT_TYPE_TEXT,
    description: 'first name',
    placeholder: 'First Name'
  },
  {
    name: FORM_FIELD_LAST_NAME,
    type: INPUT_TYPE_TEXT,
    description: 'last name',
    placeholder: 'Last Name'
  },
  {
    name: FORM_FIELD_EMAIL,
    type: INPUT_TYPE_EMAIL,
    description: 'email',
    placeholder: 'Email',
    validateFunc: (val: string) =>
      !val || isValidEmail(val) ? null : 'Please enter a valid email address'
  },
  {
    name: FORM_FIELD_PHONE,
    type: INPUT_TYPE_PHONE,
    description: 'phone number',
    placeholder: 'Phone Number',
    validateFunc: (val: string) => {
      return !val || val.match(REGEX_VALIDATE_PHONE)
        ? null
        : 'Please enter a valid phone number'
    }
  },
  {
    name: FORM_FIELD_ZIP_CODE,
    type: INPUT_TYPE_TEXT,
    description: 'zip / postal code',
    placeholder: 'Zip / Postal Code',
    validateFunc: (val: string) =>
      !val || isUSPostalCode(val) || isCanadaPostalCode(val)
        ? null
        : 'Please enter a valid postal code'
  },
  {
    name: FORM_FIELD_CONTACT_METHOD,
    id: FORM_FIELD_CONTACT_METHOD,
    component: INPUT_NODE_SELECT,
    description: 'preferred contact/alert method',
    children: (
      <Fragment>
        <option value="">Preferred method of contact...</option>
        <option value={CONTACT_METHOD_EMAIL}>Email</option>
        <option value={CONTACT_METHOD_TEXT}>Text Message</option>
      </Fragment>
    )
  },
  {
    name: FORM_FIELD_MESSAGE,
    component: INPUT_NODE_TEXTAREA,
    description: 'optional message',
    placeholder: 'Message (Optional)'
  }
] as Array<ModalLeadFormFieldDefinitionType>

const ACCEPT_TERMS_FIELD_DEFINITION = {
  name: FORM_FIELD_ACCEPT_TERMS,
  initialValue: false,
  validateFunc: (val: boolean, values: LeadContactType) =>
    val || !Boolean(values.phone)
      ? null
      : 'You must accept the terms and conditions'
} as ModalLeadFormFieldDefinitionType

/**
 * getDefaultFormDataSpec
 * @description returns the default form data spec for the given form name
 * @param {String} formName - the name of the form
 * @return {Object} - the default form data spec for the given form name
 */
export const getDefaultFormDataSpec = (
  formName: string
): LeadFormSpecResponseType =>
  formName.toLowerCase().includes(TEXT_ME)
    ? DEFAULT_FORM_SPEC_TEXT_ME_THIS_INFO
    : DEFAULT_FORM_SPEC

/**
 * getFieldSpecFromFormSpec
 * @description returns the portion of the form data spec covering the given field name
 * @param {Object} formDataSpec - a single form data spec (should come from mvh endpoint)
 * @param {String} fieldName - the name of the field to return a specific form
 * @return {Object} - the specification for the requested field
 */
export const getFieldSpecFromFormSpec = (
  formDataSpec: LeadFormSpecResponseType,
  fieldName: string
): LeadFormSpecFieldType | undefined =>
  formDataSpec.fields.find(fieldSpecCur => fieldSpecCur.name === fieldName)

/**
 * getModalLeadFormFieldDefinitions
 * @description returns a filtered version of MODAL_LEAD_FORM_FIELD_DEFINITIONS containing only the desired field names
 * @param {Array} desiredFields - an array of strings whose names are compared to field definitions for filtering
 * @param {Object} formDataSpec - a single form data spec (should come from endpoint)
 * @return {Array} - the array of fields from MODAL_LEAD_FORM_FIELD_DEFINITIONS found in the provided form data spec
 */
export const getModalLeadFormFieldDefinitions = (
  desiredFields: Array<string>,
  formDataSpec: LeadFormSpecResponseType
): Array<ModalLeadFormFieldDefinitionType> =>
  MODAL_LEAD_FORM_FIELD_DEFINITIONS.filter(
    ({ name }) =>
      desiredFields.includes(name) ||
      (
        formDataSpec.fields.find(
          ({ name: fieldName }) => fieldName === name
        ) || {
          required: 'false'
        }
      ).required !== 'false'
  )

/**
 * getModalLeadFormFieldInitialValues
 * @description returns an initial formik values object for all the fields which should be in the form (desiredFields)
 * @param {Array} desiredFields - an array of field names to include in the form
 * @param {Object} formDataSpec - a single form data spec (should come from mvh endpoint)
 * @param {Object} initialFormValues - an optional mapping of original values for the form fields
 * @return {Object} - the initial set of formik values for the form
 */
export const getModalLeadFormFieldInitialValues = (
  desiredFields: Array<string>,
  formDataSpec: LeadFormSpecResponseType,
  /* webpack-strip-block:removed */
): LeadContactType =>
  getModalLeadFormFieldDefinitions(desiredFields, formDataSpec)
    .concat(ACCEPT_TERMS_FIELD_DEFINITION)
    .reduce((initialValues, { initialValue, name }) => {
      initialValues[name] =
        // eslint-disable-next-line prettier/prettier
        initialValue ||
        /* webpack-strip-block:removed */
        ''
      return initialValues
    }, {} as Record<string, string | boolean>)

/**
 * getModalLeadFormDisclaimerText
 * @description returns the disclaimer text to use on the form
 * @param {String} accountName - the name of the dealer
 * @param {Object} formDataSpec - a single form data spec (should come from mvh endpoint)
 * @return {String} - the disclaimer text to use
 */
export const getModalLeadFormDisclaimerText = (
  accountName: string,
  formDataSpec: LeadFormSpecResponseType
): string => {
  const disclaimerFromFormSpec = getFieldSpecFromFormSpec(
    formDataSpec,
    FORM_FIELD_TERMS_CONDITIONS
  )
  debugLogForms(
    'using default disclaimer',
    !disclaimerFromFormSpec || !_isArray(disclaimerFromFormSpec.values)
  )
  return disclaimerFromFormSpec && _isArray(disclaimerFromFormSpec.values)
    ? disclaimerFromFormSpec.values.join('')
    : `By submitting my mobile phone number and/or email address to the Dealership, I hereby consent to receive\
 automated text messages, emails, and/or phone calls (which may be recorded for quality purposes) from the Dealership\
 and its affiliates in the future ("Communications"). I understand that my consent to receive Communications is not a\
 requirement to purchase any product or service and that I can opt out at any time. I understand that I may opt out of\
 receiving any further Communications by contacting ${accountName} directly, or as indicated in a text message, email,\
 or phone call I receive. I understand that I will be responsible for any expenses imposed by my mobile service\
 provider as a result of receiving text messages or phone calls.`
}

/**
 * isRequiredByFormSpec
 * @description returns whether the given field name is required by the form data spec
 * @param {Object} formDataSpec - a single form data spec (should come from mvh endpoint)
 * @param {String} fieldName - the name of the field in question
 * @param {String} fieldDescription - the description of the field in question
 * @param {Object} values - the set of values for all form entries, as provided by formik
 * @return {String} - details on the requirement which isn't met, or empty string if no requirements missed
 */
export const isRequiredByFormSpec = (
  formDataSpec: LeadFormSpecResponseType,
  fieldName: string,
  fieldDescription: string,
  values: LeadContactType
): string => {
  // unfortunately, the form spec and lead submit endpoint differ on the casing of the postal code
  // (lead form spec is camel-cased)
  if (fieldName === 'postalcode') {
    fieldName = 'postalCode'
  }
  const formSpec = getFieldSpecFromFormSpec(formDataSpec, fieldName)
  let requiredString = `Your ${fieldDescription} is required`
  // it looks like, based on Jim's sample yaml files, that "required" is not provided unless false or special value
  let isRequired = formSpec ? formSpec.required : false
  if (_isUndefined(isRequired) || _isNull(isRequired)) {
    isRequired = true
  } else if (isRequired === 'false') {
    isRequired = false
  }

  switch (isRequired) {
    case true:
      isRequired = true
      break
    case false:
      isRequired = false
      break
    case EMAIL_OR_PHONE:
      // first, make sure that the contact method isn't set to email or phone and set requirement accordingly
      switch (fieldName) {
        case FORM_FIELD_EMAIL:
          isRequired =
            values[FORM_FIELD_CONTACT_METHOD] ===
            CONTACT_METHOD_EMAIL.toString()
          break
        case FORM_FIELD_PHONE:
          isRequired =
            values[FORM_FIELD_CONTACT_METHOD] === CONTACT_METHOD_TEXT.toString()
          break
        default:
        // do nothing
      }

      if (isRequired !== true) {
        // if this is true, then the contact method didn't specify this contact type, either one is okay
        isRequired =
          !Boolean(values[FORM_FIELD_EMAIL]) &&
          !Boolean(values[FORM_FIELD_PHONE])
        requiredString = 'Your phone number or email is required'
      }
      break
    case IF_SPECIFIED:
      switch (fieldName) {
        case FORM_FIELD_EMAIL:
          isRequired =
            values[FORM_FIELD_CONTACT_METHOD] ===
            CONTACT_METHOD_EMAIL.toString()
          break
        case FORM_FIELD_PHONE:
          isRequired =
            values[FORM_FIELD_CONTACT_METHOD] === CONTACT_METHOD_TEXT.toString()
          break
        default:
        // do nothing, handle below
      }
      break
    default:
    // do nothing, handle below
  }

  if (!_isBoolean(isRequired)) {
    debugLogForms(
      'unable to handle required value for',
      fieldName,
      'from spec',
      formSpec
    )
    isRequired = true
  }

  return isRequired ? requiredString : ''
}

/**
 * validateModalLeadFormFieldValues
 * @description performs validation against the a formik values object for all fields in the form (desiredFields)
 * @param {Array} desiredFields - an array of field names to include in the form
 * @param {Object} formDataSpec - a single form data spec (should come from mvh endpoint)
 * @param {Object} values - a formik values object with the current values of all the form contents
 * @return {Object} - an object containing all the error(s) in the form values, with the field name as the key and
 *   an error string as the value
 */
export const validateModalLeadFormFieldValues = (
  desiredFields: Array<string>,
  formDataSpec: LeadFormSpecResponseType,
  requirePhone: boolean
) => (values: LeadContactType): Record<string, string> =>
  getModalLeadFormFieldDefinitions(desiredFields, formDataSpec)
    .concat(ACCEPT_TERMS_FIELD_DEFINITION)
    .reduce((errors, { description, name, validateFunc }) => {
      let requiredString = isRequiredByFormSpec(
        formDataSpec,
        name,
        description,
        values
      )

      if (requirePhone && name === FORM_FIELD_PHONE && !requiredString) {
        requiredString = 'Your phone number is required'
      }

      // @ts-ignore
      const valueCur = values[name]

      if (requiredString && !valueCur) {
        errors[name] = requiredString
      }

      if (validateFunc) {
        const result = validateFunc(valueCur, values)
        if (_isString(result)) {
          errors[name] = result
          debugLogForms('setting form submit error', name, result, valueCur)
        }
      }
      return errors
    }, {} as Record<string, string>)
